// Окно технических работ (переезд на Selectel и т.п.): всплывающее предупреждение заранее и заглушка на время
// работ. Источник — статический /maintenance.json на сервере фронта (public/maintenance.json): он отвечает,
// даже когда API заморожен или переключается, и меняется правкой файла на сервере, без сборки.
// Порядок работы с файлом — docs/SELECTEL_CUTOVER_PREP.md §24.
//
// Заглушка — только представление: запись в окне закрывает сервер (app_write_freeze), обход заглушки
// (?maintenance=bypass) нужен для проверки стенда после переключения и ничего не открывает.

export type MaintenanceForce = 'on' | 'off'

export interface MaintenanceConfig {
  /** Метка окна: от неё зависит ключ «скрыть предупреждение на сегодня». */
  id: string
  /** Начало и плановый конец работ, ISO с часовым поясом (например, 2026-10-29T22:00:00+03:00). */
  startsAt: string | null
  endsAt: string | null
  /** За сколько часов до начала показывать предупреждение (по умолчанию 72). */
  noticeHours?: number
  /** 'on' — заглушка сейчас, независимо от времени (работы затянулись); 'off' — окна нет (отменено, закончили раньше). */
  force?: MaintenanceForce | null
  /** Необязательная строка вместо стандартного пояснения. */
  message?: string | null
}

export type MaintenancePhase = 'none' | 'notice' | 'maintenance'

export const DEFAULT_NOTICE_HOURS = 72
export const MAINTENANCE_BYPASS_QUERY = 'bypass'
export const MAINTENANCE_BYPASS_START_PARAM = 'maintenance_bypass'

const HOUR_MS = 60 * 60 * 1000
const MOSCOW_TZ = 'Europe/Moscow'

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

export function parseMaintenanceConfig(raw: unknown): MaintenanceConfig | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const force = r.force === 'on' || r.force === 'off' ? r.force : null
  const noticeHours = typeof r.noticeHours === 'number' && Number.isFinite(r.noticeHours) && r.noticeHours >= 0
    ? r.noticeHours
    : DEFAULT_NOTICE_HOURS
  return {
    id: stringOrNull(r.id) ?? '',
    startsAt: stringOrNull(r.startsAt),
    endsAt: stringOrNull(r.endsAt),
    noticeHours,
    force,
    message: stringOrNull(r.message),
  }
}

export function resolveMaintenancePhase(config: MaintenanceConfig | null, nowMs: number): MaintenancePhase {
  if (!config || config.force === 'off') return 'none'
  if (config.force === 'on') return 'maintenance'
  const start = config.startsAt ? Date.parse(config.startsAt) : NaN
  if (Number.isNaN(start)) return 'none'
  const end = config.endsAt ? Date.parse(config.endsAt) : NaN
  if (nowMs >= start) {
    // Без конца окно держится до force: 'off' — лучше лишняя заглушка, чем ошибки записи в окне.
    return Number.isNaN(end) || nowMs < end ? 'maintenance' : 'none'
  }
  const noticeHours = config.noticeHours ?? DEFAULT_NOTICE_HOURS
  return nowMs >= start - noticeHours * HOUR_MS ? 'notice' : 'none'
}

function moscowParts(ms: number) {
  const parts = new Intl.DateTimeFormat('ru-RU', {
    timeZone: MOSCOW_TZ,
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms))
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
  return { date: `${get('day')} ${get('month')}`, time: `${get('hour')}:${get('minute')}` }
}

/** «29 октября с 22:00 до 02:00 (30 октября)» по Москве; null — окно не задано. */
export function formatMaintenanceWindow(config: MaintenanceConfig | null): string | null {
  const start = config?.startsAt ? Date.parse(config.startsAt) : NaN
  if (Number.isNaN(start)) return null
  const s = moscowParts(start)
  const end = config?.endsAt ? Date.parse(config.endsAt) : NaN
  if (Number.isNaN(end)) return `${s.date} с ${s.time}`
  const e = moscowParts(end)
  return e.date === s.date ? `${s.date} с ${s.time} до ${e.time}` : `${s.date} с ${s.time} до ${e.time} (${e.date})`
}

/** «02:00 30 октября» по Москве — ориентир окончания на заглушке. */
export function formatMaintenanceEnd(config: MaintenanceConfig | null): string | null {
  const end = config?.endsAt ? Date.parse(config.endsAt) : NaN
  if (Number.isNaN(end)) return null
  const e = moscowParts(end)
  return `${e.time} ${e.date}`
}

/** Календарный день по Москве (YYYY-MM-DD): предупреждение скрывается до конца этого дня. */
export function moscowDay(ms: number): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: MOSCOW_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(ms))
}

export function maintenanceNoticeDismissKey(config: MaintenanceConfig): string {
  return `maintenance_notice_dismissed:${config.id || config.startsAt || 'window'}`
}

export function isMaintenanceBypassRequested(search: string, startParam: string | null): boolean {
  return new URLSearchParams(search).get('maintenance') === MAINTENANCE_BYPASS_QUERY
    || startParam === MAINTENANCE_BYPASS_START_PARAM
}

/** Поправка на часы устройства по заголовку Date ответа сервера; мелкие расхождения не учитываются. */
export function clockSkewMs(serverDateHeader: string | null, localNowMs: number): number {
  const server = serverDateHeader ? Date.parse(serverDateHeader) : NaN
  if (Number.isNaN(server)) return 0
  const skew = server - localNowMs
  return Math.abs(skew) > 60_000 ? skew : 0
}
